// Angular Files
import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Input,
    NgModule,
    OnInit,
    Provider,
    QueryList,
    ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const CODE_INPUT_VALUE_ACCESSOR: Provider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CodeInputComponent),
    multi: true
};

@Component({
    selector: 'app-code-input',
    templateUrl: './code-input.component.html',
    styleUrls: ['./code-input.component.scss'],
    host: {
        class: 'code-input'
    },
    standalone: false,
    providers: [CODE_INPUT_VALUE_ACCESSOR]
})
export class CodeInputComponent implements ControlValueAccessor, OnInit {
    // Declare @Input variables
    @Input() length: number = 6;

    @Input('aria-label') ariaLabel: string = 'Verification code';

    @ViewChildren('cell') inputs!: QueryList<ElementRef<HTMLInputElement>>;

    // Public variables
    public digits: string[] = [];

    public disabled: boolean = false;

    // Private variables
    private _onChange: (value: string) => void = () => {};

    private _onTouched: () => void = () => {};

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.digits = Array.from({ length: this.length }, () => '');
    }

    writeValue(value: string): void {
        const chars = (value ?? '').replace(/\D/g, '').slice(0, this.length).split('');
        this.digits = Array.from({ length: this.length }, (_, i) => chars[i] ?? '');
        this._cdr.markForCheck();
    }

    registerOnChange(fn: (value: string) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this._cdr.markForCheck();
    }

    public onInput_setDigit(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const digit = input.value.replace(/\D/g, '').slice(-1);

        this.digits[index] = digit;
        input.value = digit;

        this._emit();

        if (digit && index < this.length - 1) {
            this._focus(index + 1);
        }
    }

    public onKeydown_navigate(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
            this._focus(index - 1);
        } else if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this._focus(index - 1);
        } else if (event.key === 'ArrowRight' && index < this.length - 1) {
            event.preventDefault();
            this._focus(index + 1);
        }
    }

    public onPaste_distribute(event: ClipboardEvent): void {
        event.preventDefault();

        const pasted = (event.clipboardData?.getData('text') ?? '')
            .replace(/\D/g, '')
            .slice(0, this.length)
            .split('');

        if (!pasted.length) return;

        this.digits = Array.from({ length: this.length }, (_, i) => pasted[i] ?? '');
        this._emit();
        this._focus(Math.min(pasted.length, this.length - 1));
    }

    private _focus(index: number): void {
        this.inputs.get(index)?.nativeElement.focus();
    }

    public onBlur(): void {
        // Mark touched only when focus leaves the entire group, not when moving between cells
        setTimeout(() => {
            const active = document.activeElement;
            if (!this.inputs.some(ref => ref.nativeElement === active)) {
                this._onTouched();
            }
        });
    }

    private _emit(): void {
        this._onChange(this.digits.join(''));
    }
}

@NgModule({
    imports: [CommonModule],
    declarations: [CodeInputComponent],
    exports: [CodeInputComponent]
})
export class CodeInputModule { }
